/**
 * Language Detection Utilities
 *
 * Minimal utilities for detecting browser language and managing locale preferences
 * without React Context dependency.
 */

export type Locale = 'en' | 'sv';

/**
 * Get browser's preferred language
 * @returns 'sv' for Swedish browsers, 'en' for all others
 */
export function getBrowserLanguage(): Locale {
  if (typeof navigator === 'undefined') {
    return 'sv'; // Default for SSR
  }

  const browserLang = navigator.language.toLowerCase();

  if (browserLang.startsWith('sv')) {
    return 'sv';
  }

  return 'en'; // Default to English for all other languages
}

/**
 * Get stored language preference from localStorage
 * @returns Stored locale ('en' or 'sv'), or null if not found/invalid
 */
export function getStoredLanguage(): Locale | null {
  try {
    const stored = localStorage.getItem('ssn-locale');
    if (stored === 'en' || stored === 'sv') {
      return stored;
    }
  } catch (error) {
    console.error('Error accessing localStorage:', error);
  }
  return null;
}

/**
 * Set language preference in localStorage
 * @param locale - The locale to store
 */
export function setStoredLanguage(locale: Locale): void {
  try {
    localStorage.setItem('ssn-locale', locale);
  } catch (error) {
    console.error('Error saving locale to localStorage:', error);
  }
}

/**
 * Get the preferred locale based on stored preference or browser language
 * @returns The preferred locale ('sv' or 'en')
 */
export function getPreferredLocale(): Locale {
  const stored = getStoredLanguage();
  if (stored) {
    return stored;
  }
  return getBrowserLanguage();
}
